import { Link } from "react-router-dom";
import { useState } from "react";
import jsgLogo from "@/assets/jsg-logo.webp";

const ProposalFooter = () => {
  const [email, setEmail] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmail("");
  };

  return (
    <footer className="relative pt-[100px] pb-10 px-6 md:px-[60px] max-w-[1320px] mx-auto">
      {/* Top accent line */}
      <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Ambient glow */}
      <div className="absolute -top-[10%] left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse,hsla(27,95%,64%,0.15)_0%,transparent_70%)] pointer-events-none opacity-20" />

      {/* Main grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-12 lg:gap-[60px] mb-[72px] relative">
        {/* Brand */}
        <div className="max-w-[360px] md:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-[10px] overflow-hidden">
              <img src={jsgLogo} alt="JSG Solutions" className="w-full h-full object-cover" />
            </div>
            <span className="font-['Montserrat'] font-bold text-xl tracking-tight text-foreground">JSG Solutions Group</span>
          </div>
          <p className="text-sm leading-[1.7] text-muted-foreground font-light mb-7">
            AI agents for local businesses — built to save time and drive growth.
          </p>

          <ul className="flex flex-col gap-3">
            <li className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-primary stroke-2 fill-none shrink-0">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <a href="tel:+447446982538" className="hover:text-primary transition-colors">+44 7446 982538</a>
            </li>
            <li className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-primary stroke-2 fill-none shrink-0">
                <path d="M4 4h16c1 0 2 1 2 2v12c0 1-1 2-2 2H4c-1 0-2-1-2-2V6c0-1 1-2 2-2z" />
                <path d="M22 6l-10 7L2 6" />
              </svg>
              <a href="mailto:services@jsgroup.ai" className="hover:text-primary transition-colors">services@jsgroup.ai</a>
            </li>
            <li className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-primary stroke-2 fill-none shrink-0">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              London, United Kingdom
            </li>
          </ul>
        </div>

        {/* Product */}
        <div>
          <p className="font-['Montserrat'] text-xs font-semibold tracking-[2px] uppercase text-primary mb-6 flex items-center gap-2.5">
            <span className="w-5 h-px bg-primary" />
            AI AGENTS
          </p>
          <ul className="flex flex-col gap-3.5">
            {[
              { label: "Voice", to: "/voice-ai" },
              { label: "Chatbot", to: "/chat-ai" },
              { label: "Lead Qualification", to: "/lead-qualification" },
              
              { label: "Workflow Automation", to: "#" },
            ].map((item) => (
              <li key={item.label}>
                {item.to === "#" ? (
                  <a href="#" className="group/link text-sm text-muted-foreground hover:text-foreground transition-all duration-300 inline-flex items-center">
                    <span className="w-0 h-px bg-primary mr-0 group-hover/link:w-3.5 group-hover/link:mr-2.5 transition-all duration-300" />
                    {item.label}
                  </a>
                ) : (
                  <Link to={item.to} className="group/link text-sm text-muted-foreground hover:text-foreground transition-all duration-300 inline-flex items-center">
                    <span className="w-0 h-px bg-primary mr-0 group-hover/link:w-3.5 group-hover/link:mr-2.5 transition-all duration-300" />
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <p className="font-['Montserrat'] text-xs font-semibold tracking-[2px] uppercase text-primary mb-6 flex items-center gap-2.5">
            <span className="w-5 h-px bg-primary" />
            Company
          </p>
          <ul className="flex flex-col gap-3.5">
            {[
              { label: "Overview", to: "/" },
              { label: "About", to: "/about" },
              { label: "Contact", to: "/contact" },
              { label: "Book a Demo", to: "/book-demo" },
            ].map((item) => (
              <li key={item.label}>
                <Link to={item.to} className="group/link text-sm text-muted-foreground hover:text-foreground transition-all duration-300 inline-flex items-center">
                  <span className="w-0 h-px bg-primary mr-0 group-hover/link:w-3.5 group-hover/link:mr-2.5 transition-all duration-300" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter */}
        <div className="md:col-span-2 lg:col-span-1">
          <p className="font-['Montserrat'] text-xs font-semibold tracking-[2px] uppercase text-primary mb-6 flex items-center gap-2.5">
            <span className="w-5 h-px bg-primary" />
            Stay Updated
          </p>
          <p className="text-sm leading-[1.65] text-muted-foreground font-light mb-5">
            Monthly insights on AI automation for local businesses. No spam, unsubscribe anytime.
          </p>
          <form onSubmit={handleSubmit} className="relative bg-card border border-border rounded-xl p-[5px] pl-5 flex items-center focus-within:border-primary/30 transition-colors">
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-foreground font-['Montserrat'] text-[13px] font-normal py-2.5 px-2"
            />
            <button
              type="submit"
              className="bg-primary border-none rounded-xl py-2.5 px-5 text-primary-foreground font-['Montserrat'] text-[13px] font-semibold cursor-pointer transition-all duration-300 flex items-center gap-1.5 hover:shadow-[0_4px_20px_hsla(27,95%,64%,0.3)] hover:-translate-y-0.5"
            >
              Subscribe
              <svg viewBox="0 0 24 24" className="w-[13px] h-[13px] stroke-primary-foreground stroke-[2.5] fill-none">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </form>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 flex-wrap relative">
        <div className="text-[13px] text-muted-foreground/80">
          © {new Date().getFullYear()} <span className="text-muted-foreground">JSG Solutions Group Ltd.</span> All rights reserved.
        </div>

        <ul className="flex items-center gap-7 flex-wrap">
          {["Terms & Conditions", "Privacy Policy"].map((item, i) => (
            <li key={item} className="relative">
              <a href="#" className="text-[13px] text-muted-foreground hover:text-primary transition-colors">
                {item}
              </a>
              {i < 1 && (
                <span className="absolute -right-[14px] top-1/2 -translate-y-1/2 w-0.5 h-0.5 rounded-full bg-muted-foreground/60" />
              )}
            </li>
          ))}
        </ul>

        <ul className="flex gap-2.5">
          {[
            { label: "Instagram", icon: <><rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></> },
            { label: "LinkedIn", icon: <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></> },
            { label: "X", icon: <path d="M18 6L6 18M6 6l12 12" /> },
          ].map((social) => (
            <li key={social.label}>
              <a
                href="#"
                aria-label={social.label}
                className="w-[38px] h-[38px] rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary/30 hover:text-primary hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-300"
              >
                <svg viewBox="0 0 24 24" className="w-[15px] h-[15px] stroke-current stroke-[1.8] fill-none">
                  {social.icon}
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </footer>
  );
};

export default ProposalFooter;
